import { useMemo, useRef } from 'react'
import { useI18n } from '../provider/i18n'
import { ConfirmDialogType } from './ConfirmDialog'
import {
  BloodlabInterpretationLink,
  HolterDoDomuLink,
  StorageUrl,
} from '../const'
import BloodlabLogo from '../assets/bloodlab-logo.svg'
import HolterDoDomuLogo from '../assets/holter-do-domu-logo.png'
import { TailSpin } from 'react-loader-spinner'
import { Blue300 } from '../lib/colors'
import { useAtomValue } from 'jotai'
import { availableLaboratoriesSubscriptionAtom } from '../store/subscription.atoms'
import { GetAvailableLaboratoriesSubscription } from '../generated/urql.client'
import { isShowHolterAndBloodlabEnabledAtom } from '../store/featureFlags.atoms'

const AvailableLaboratories = () => {
  const i18n = useI18n()
  const confirmDialogRef = useRef<ConfirmDialogType>(null)
  const [
    {
      data: availableLaboratoriesData,
      fetching: availableLaboratoriesFetching,
    },
  ] = useAtomValue(availableLaboratoriesSubscriptionAtom)
  const isShowHolterAndBloodlabEnabled = useAtomValue(
    isShowHolterAndBloodlabEnabledAtom
  )

  const availableLaboratoriesWithLogo = useMemo(
    () =>
      (availableLaboratoriesData?.available_laboratory || []).filter(
        (
          laboratory
        ): laboratory is GetAvailableLaboratoriesSubscription['available_laboratory'][number] & {
          logoFile: { id: number }
        } => !!laboratory.logoFile
      ),
    [availableLaboratoriesData]
  )

  return (
    <div className="max-w-[200px]">
      <div className="flex items-center gap-1 mb-5">
        <p className="text-blue-700">
          {i18n.t('screens.catalog.partners.title')}
        </p>
      </div>
      <ul className="flex flex-row md:flex-col gap-5 overflow-x-scroll md:overflow-visible p-5 -m-5">
        {isShowHolterAndBloodlabEnabled && (
          <>
            <li
              className="hl-panel h-24 flex items-center justify-center cursor-pointer min-w-40"
              onClick={() => {
                confirmDialogRef.current?.show({
                  title: i18n.t('screens.catalog.bloodlab.dialog.title'),
                  message: i18n.t('screens.catalog.bloodlab.dialog.message'),
                  successText: i18n.t(
                    'screens.catalog.bloodlab.dialog.button.ok'
                  ),
                  cancelText: i18n.t(
                    'screens.catalog.bloodlab.dialog.button.learnMore'
                  ),
                  onCancel: () => {
                    window.open(BloodlabInterpretationLink, '_blank')
                  },
                })
              }}
            >
              <img src={BloodlabLogo} alt="Bloodlab" width={136} height={76} />
            </li>
            <li
              className="hl-panel flex items-center justify-center cursor-pointer min-w-40 h-24"
              onClick={() => window.open(HolterDoDomuLink, '_blank')}
            >
              <img
                src={HolterDoDomuLogo}
                alt="Holter Do Domu"
                width={136}
                height={76}
              />
            </li>
          </>
        )}
        {availableLaboratoriesFetching ? (
          <TailSpin
            wrapperClass="flex justify-center items-center"
            color={Blue300}
            height={24}
            width={24}
          />
        ) : (
          availableLaboratoriesWithLogo.map((laboratory) => (
            <li
              key={laboratory.id}
              className="hl-panel flex items-center justify-center min-w-40 h-24"
            >
              {laboratory.url ? (
                <a
                  href={laboratory.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    alt={laboratory.name}
                    width={136}
                    height={76}
                    src={`${StorageUrl}/files/${laboratory.logoFile.id}`}
                  />
                </a>
              ) : (
                <img
                  alt={laboratory.name}
                  width={136}
                  height={76}
                  src={`${StorageUrl}/files/${laboratory.logoFile.id}`}
                />
              )}
            </li>
          ))
        )}
      </ul>
    </div>
  )
}

export default AvailableLaboratories
